// frontend/src/components/Dashboard/AbilitiesTab.js
import React, { useState, useEffect, useMemo } from 'react';
import { abilitiesAPI } from '../../services/api';

function AbilitiesTab({ character, abilities, setAbilities, availableXP, stats, onBrowserToggle }) {
  const [allAbilities, setAllAbilities] = useState({});
  const [selectedStat, setSelectedStat] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | passive | active
  const [purchaseError, setPurchaseError] = useState(null);

  const browserOpen = selectedStat !== null;

  // Notify parent when browser opens/closes
  useEffect(() => {
    onBrowserToggle?.(browserOpen);
    return () => onBrowserToggle?.(false);
  }, [browserOpen, onBrowserToggle]);

  // Load abilities when stat selected
  useEffect(() => {
    if (selectedStat) loadAbilities(selectedStat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStat]);

  const statNames = {
    resilience: 'Resilience',
    physique: 'Physique',
    coordination: 'Coordination',
    perception: 'Perception',
    presence: 'Presence',
    logic: 'Logic',
  };

  const baselineAbilityNames = useMemo(() => {
    try {
      const parsed =
        typeof character?.forges === 'string' ? JSON.parse(character.forges) : character?.forges || {};
      return new Set(Object.keys(parsed || {}));
    } catch {
      return new Set();
    }
  }, [character]);

  // ---- schema helpers (supports both camelCase and snake_case) ----
  const getTierXpCost = (tier) => Number(tier?.xpCost ?? tier?.xp_cost ?? 0) || 0;
  const getTierStatRequirement = (tier) => Number(tier?.statRequirement ?? tier?.stat_requirement ?? 0) || 0;
  const getTierChargeCost = (tier) => tier?.chargeCost ?? tier?.charge_cost ?? null;
  const getTierType = (tier) => tier?.type ?? tier?.ability_type ?? null;
  const getTierIsSustained = (tier) => !!(tier?.isSustained ?? tier?.is_sustained);
  const getTierChargeCostPerRound = (tier) => tier?.chargeCostPerRound ?? tier?.charge_cost_per_round ?? null;
  const getTierDurationText = (tier) => tier?.durationText ?? tier?.duration_text ?? null;
  const getTierEffectsMd = (tier) => tier?.effects_md ?? tier?.effectsMd ?? '';
  const getTierPrereqs = (tier) => tier?.prerequisites ?? tier?.prereqs ?? [];

  // Owned index (prefer code, fallback to name-derived code)
  const ownedByCode = useMemo(() => {
    const map = new Map();
    for (const a of abilities || []) {
      const code = a.code || toCode(a.name);
      const tier = Number.isFinite(a.tier) ? a.tier : 1;
      map.set(code, { ...a, code, tier });
    }
    return map;
  }, [abilities]);

  const loadAbilities = async (statKey) => {
    if (!statKey) return;
    try {
      setIsLoading(true);

      const owned = (abilities || []).map((a) => a.code || a.name).join(',');

      const res = await abilitiesAPI.getByStat(statKey, {
        characterStat: stats[statKey] || 1,
        availableXP,
        ownedAbilities: owned,
      });

      setAllAbilities((prev) => ({
        ...prev,
        [statKey]: res.data.abilities || [],
      }));
    } catch (error) {
      console.error('Failed to load abilities:', error);
      setAllAbilities((prev) => ({ ...prev, [statKey]: [] }));
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeStatKey = (s) => {
    const t = String(s || '').trim().toLowerCase();
    if (!t) return t;
    if (t === 'coordination') return 'coordination';
    if (t === 'physique') return 'physique';
    if (t === 'logic') return 'logic';
    if (t === 'perception') return 'perception';
    if (t === 'presence') return 'presence';
    if (t === 'resilience') return 'resilience';
    return t;
  };

  const getOwnedTier = (ability) => {
    const code = ability.code || toCode(ability.name);
    return ownedByCode.get(code)?.tier || 0;
  };

  const getSortedTiers = (ability) => {
    const tiers = Array.isArray(ability?.tiers) ? ability.tiers : [];
    return [...tiers].sort((a, b) => (a.tier || 0) - (b.tier || 0));
  };

  const hasMultipleTiers = (abilityLike) => {
    const tiers = Array.isArray(abilityLike?.tiers) ? abilityLike.tiers : [];
    return tiers.length > 1;
  };

  const getNextPurchasableTier = (ability) => {
    const ownedTier = getOwnedTier(ability);
    const tiers = getSortedTiers(ability);
    if (!tiers.length) return null;
    return tiers.find((t) => t.tier === ownedTier + 1) || null;
  };

  const canPurchaseTier = (ability, tier) => {
    if (!tier) return { can: false, reason: 'no_tier' };

    const ownedTier = getOwnedTier(ability);
    if (tier.tier <= ownedTier) return { can: false, reason: 'already_owned' };

    const statKey = normalizeStatKey(ability.stat || selectedStat);
    const statValue = stats[statKey] || 1;

    const req = getTierStatRequirement(tier);
    if (statValue < req) {
      return {
        can: false,
        reason: 'stat_requirement',
        message: `Requires ${statNames[statKey] || ability.stat} ${req}. You have ${statValue}.`,
      };
    }

    const xp = getTierXpCost(tier);
    if (xp > availableXP) {
      return {
        can: false,
        reason: 'insufficient_xp',
        message: `Not enough XP! Need ${xp} XP, you have ${availableXP} XP available.`,
      };
    }

    return { can: true };
  };

  const purchaseOrUpgrade = (ability) => {
    setPurchaseError(null);

    const code = ability.code || toCode(ability.name);
    const owned = ownedByCode.get(code);

    const nextTier = getNextPurchasableTier(ability);
    const check = canPurchaseTier(ability, nextTier);

    if (!check.can) {
      if (check.reason === 'already_owned') {
        setPurchaseError('You already have this tier!');
      } else if (check.message) {
        setPurchaseError(check.message);
      } else {
        setPurchaseError('Unable to purchase this tier.');
      }
      setTimeout(() => setPurchaseError(null), 3000);
      return;
    }

    const statKey = normalizeStatKey(ability.stat || selectedStat);

    const descriptionText = stripMd(ability.description_md || '');
    const effectsText = stripMd(getTierEffectsMd(nextTier) || '');

    const stored = {
      code,
      name: ability.name,
      stat: statKey,
      tier: nextTier.tier,
      type: String(getTierType(nextTier) || '').toUpperCase() === 'ACTIVE' ? 'Active' : 'Passive',
      statRequirement: getTierStatRequirement(nextTier),
      xpCost: getTierXpCost(nextTier),
      chargeCost: getTierChargeCost(nextTier),
      isSustained: getTierIsSustained(nextTier),
      chargeCostPerRound: getTierChargeCostPerRound(nextTier),
      durationText: getTierDurationText(nextTier),

      // ✅ store both for owned cards
      description: descriptionText || '',
      effects: effectsText || '',

      prerequisites: formatPrereqs(getTierPrereqs(nextTier) || []),
    };

    if (!owned) {
      setAbilities((prev) => [...prev, stored]);
      setPurchaseError(`✓ Purchased ${ability.name} (Tier ${nextTier.tier})!`);
    } else {
      setAbilities((prev) =>
        prev.map((a) => {
          const aCode = a.code || toCode(a.name);
          if (aCode !== code) return a;
          return { ...a, ...stored };
        })
      );
      setPurchaseError(`✓ Upgraded ${ability.name} to Tier ${nextTier.tier}!`);
    }

    setTimeout(() => setPurchaseError(null), 2000);
  };

  const removeAbility = (abilityKeyOrName) => {
    if (baselineAbilityNames.has(abilityKeyOrName)) {
      alert("You can't remove an ability that was already saved.");
      return;
    }
    setAbilities((prev) =>
      prev.filter((a) => (a.code || a.name) !== abilityKeyOrName && a.name !== abilityKeyOrName)
    );
  };

  const getTotalAbilitiesXP = () => {
    return (abilities || []).reduce((total, ability) => total + (ability.xpCost || 0), 0);
  };

  // ✅ Sort owned cards by xpCost (then name)
  const sortedOwnedAbilities = useMemo(() => {
    return [...(abilities || [])].sort((a, b) => {
      const ax = Number(a?.xpCost ?? 0) || 0;
      const bx = Number(b?.xpCost ?? 0) || 0;
      if (ax !== bx) return ax - bx;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [abilities]);

  // Filter + ✅ sort browser list by next purchasable xp cost (then name)
  const filteredAbilities = useMemo(() => {
    if (!selectedStat) return [];
    const list = allAbilities[selectedStat] || [];
    const q = searchTerm.trim().toLowerCase();

    const filtered = list.filter((a) => {
      if (q && !(a.name || '').toLowerCase().includes(q)) return false;

      if (filterType !== 'all') {
        const next = getNextPurchasableTier(a);
        if (!next) return false;
        const t = String(getTierType(next) || '').toLowerCase();
        if (filterType === 'active' && t !== 'active') return false;
        if (filterType === 'passive' && t !== 'passive') return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      const na = getNextPurchasableTier(a);
      const nb = getNextPurchasableTier(b);

      if (!na && nb) return 1;
      if (na && !nb) return -1;
      if (!na && !nb) return String(a?.name || '').localeCompare(String(b?.name || ''));

      const ax = getTierXpCost(na);
      const bx = getTierXpCost(nb);
      if (ax !== bx) return ax - bx;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAbilities, selectedStat, searchTerm, filterType, abilities, stats, availableXP]);

  const handleStatSelect = (stat) => {
    setPurchaseError(null);
    setSelectedStat((prev) => (prev === stat ? null : stat));
  };

  return (
    <div className={`abilities-editor ${browserOpen ? 'browser-open' : ''}`}>
      {/* Left side - Owned abilities */}
      <div className="abilities-main-section">
        <div className="section-header">
          <h3>Abilities & Forges</h3>
        </div>

        <p className="section-note">
          <strong>Ability Cost Formula:</strong> 100 XP + (Required Stat × 20 XP)
          <br />
          <strong>System:</strong> Odd levels provide passive abilities. Even levels provide active abilities requiring
          Aetherion charges.
        </p>

        <div className="xp-summary-box">
          <div className="xp-row">
            <span>Total XP Spent on Abilities:</span>
            <span className="xp-value">{getTotalAbilitiesXP()} XP</span>
          </div>
          <div className="xp-row">
            <span>Abilities Purchased:</span>
            <span className="xp-value">{abilities.length}</span>
          </div>
        </div>

        <div className="stat-filter-section">
          <h4>Select Stat to Browse Abilities</h4>
          <div className="stat-selector">
            {Object.entries(statNames).map(([key, name]) => (
              <button
                key={key}
                type="button"
                className={`stat-btn ${selectedStat === key ? 'active' : ''}`}
                onClick={() => handleStatSelect(key)}
              >
                <span className="stat-name">{name}</span>
                <span className="stat-value">{stats[key] || 1}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="purchased-abilities">
          <h4>Your Abilities</h4>

          {abilities.length === 0 ? (
            <div className="empty-state">
              <p>No abilities purchased yet.</p>
              <p className="hint">Select a stat above to browse available abilities!</p>
            </div>
          ) : (
            <div className="abilities-grid">
              {sortedOwnedAbilities.map((ability) => {
                const key = ability.code || ability.name;
                const tier = Number.isFinite(ability.tier) ? ability.tier : 1;

                // Only show tier if multi-tier. Without the full definition here, best heuristic:
                // - If tier > 1, clearly tiered
                // - Or if we stored a flag (future-proof)
                const showTier = tier > 1 || ability.isTiered === true || ability.is_tiered === true;

                const desc = stripMd(ability.description || '');
                const eff = stripMd(ability.effects || '');

                return (
                  <div key={key} className="ability-card owned">
                    <div className="ability-card-header">
                      <h5>{ability.name}</h5>
                      <button type="button" className="remove-btn" onClick={() => removeAbility(key)} title="Remove">
                        ×
                      </button>
                    </div>

                    <div className="ability-card-meta">
                      <span className="meta-badge type">{ability.type || 'Passive'}</span>
                      <span className="meta-badge cost">{ability.xpCost || 0} XP</span>
                      {showTier ? <span className="meta-badge tier">Tier {tier}</span> : null}
                      {ability.chargeCost ? (
                        <span className="meta-badge charge">Charge Cost: {ability.chargeCost}</span>
                      ) : null}
                    </div>

                    <div className="ability-card-stat">
                      {(statNames[ability.stat] || ability.stat)} {ability.statRequirement || 0}
                    </div>

                    {ability.prerequisites ? (
                      <div className="ability-card-prereq">
                        <strong>Prerequisites:</strong> {ability.prerequisites}
                      </div>
                    ) : null}

                    {/* ✅ show description + effects */}
                    {desc ? (
                      <p className="ability-card-description">
                        <strong>Description:</strong> {desc}
                      </p>
                    ) : null}

                    {eff ? (
                      <p className="ability-card-effects">
                        <strong>Effects:</strong> {eff}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right side - Browser */}
      {browserOpen && (
        <div className="abilities-browser-section">
          <div className="browser-header">
            <h3>{statNames[selectedStat]} Abilities</h3>
            <button type="button" className="close-browser-btn" onClick={() => setSelectedStat(null)}>
              ✕ Close
            </button>
          </div>

          {purchaseError && (
            <div className={`purchase-feedback ${String(purchaseError).startsWith('✓') ? 'success' : 'error'}`}>
              {purchaseError}
            </div>
          )}

          <div className="browser-filters">
            <input
              type="text"
              placeholder="Search abilities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
              <option value="all">All Types</option>
              <option value="passive">Passive Only</option>
              <option value="active">Active Only</option>
            </select>
          </div>

          <div className="browser-abilities-list">
            {isLoading ? (
              <div className="loading-state">
                <p>Loading {statNames[selectedStat]} abilities...</p>
              </div>
            ) : filteredAbilities.length === 0 ? (
              <p className="empty-message">
                {searchTerm || filterType !== 'all'
                  ? 'No abilities match your filters.'
                  : `No abilities available for ${statNames[selectedStat]}.`}
              </p>
            ) : (
              filteredAbilities.map((ability) => {
                const code = ability.code || toCode(ability.name);
                const owned = ownedByCode.get(code);
                const ownedTier = owned ? (Number.isFinite(owned.tier) ? owned.tier : 1) : 0;

                const showTier = hasMultipleTiers(ability);

                const nextTier = getNextPurchasableTier(ability);
                const check = canPurchaseTier(ability, nextTier);

                const displayType = nextTier
                  ? (String(getTierType(nextTier)).toUpperCase() === 'ACTIVE' ? 'Active' : 'Passive')
                  : '—';

                const displayXp = nextTier ? `${getTierXpCost(nextTier)} XP` : 'Maxed';
                const displayTier = nextTier ? `Tier ${nextTier.tier}` : `Tier ${ownedTier}`;

                const desc = stripMd(ability.description_md || '');
                const eff = stripMd(getTierEffectsMd(nextTier) || '');

                return (
                  <div
                    key={code}
                    className={`ability-card browsing ${owned ? 'owned' : ''} ${!check.can ? 'locked' : ''}`}
                    onClick={() => check.can && purchaseOrUpgrade(ability)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="ability-card-header">
                      <h5>{ability.name}</h5>
                      {owned && showTier ? <span className="owned-badge">✓ Tier {ownedTier}</span> : null}
                    </div>

                    <div className="ability-card-meta">
                      <span className="meta-badge type">{displayType}</span>
                      <span className={`meta-badge cost ${!check.can && !owned ? 'insufficient' : ''}`}>{displayXp}</span>
                      {showTier ? <span className="meta-badge tier">{displayTier}</span> : null}
                      {getTierChargeCost(nextTier) ? (
                        <span className="meta-badge charge">Charge Cost: {getTierChargeCost(nextTier)}</span>
                      ) : null}
                    </div>

                    {/* ✅ show description + effects */}
                    {desc ? (
                      <p className="ability-card-description">
                        <strong>Description:</strong> {desc}
                      </p>
                    ) : null}

                    {eff ? (
                      <p className="ability-card-effects">
                        <strong>Effects:</strong> {eff}
                      </p>
                    ) : null}

                    {nextTier ? (
                      <>
                        <div className="ability-card-stat">
                          Requires {statNames[selectedStat]} {getTierStatRequirement(nextTier)}
                        </div>

                        {(getTierPrereqs(nextTier) || []).length ? (
                          <div className="ability-card-prereq">
                            <strong>Prerequisites:</strong> {formatPrereqs(getTierPrereqs(nextTier))}
                          </div>
                        ) : null}

                        {!owned && check.can && (
                          <div className="purchase-hint">Click to purchase for {getTierXpCost(nextTier)} XP</div>
                        )}

                        {owned && check.can && (
                          <div className="purchase-hint">Click to upgrade to Tier {nextTier.tier}</div>
                        )}

                        {check.reason === 'insufficient_xp' && getTierXpCost(nextTier) ? (
                          <div className="purchase-warning">Need {getTierXpCost(nextTier) - availableXP} more XP</div>
                        ) : null}
                      </>
                    ) : (
                      <div className="purchase-hint">Max tier owned.</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* --- inline CSS (extended with .ability-card-effects) --- */}
      <style>{`
        .abilities-editor {
          display: flex;
          gap: 20px;
          height: 100%;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
          align-items: stretch;
        }

        .abilities-editor.browser-open {
          gap: 20px;
          align-items: stretch;
        }

        .abilities-main-section {
          flex: 1 1 0;
          min-width: 0;
          overflow-y: auto;
          padding-right: 10px;
        }

        .abilities-browser-section {
          flex: 1 1 0;
          min-width: 0;
          max-width: 100%;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .section-header { margin-bottom: 15px; }

        .section-header h3 {
          color: #c9a962;
          margin: 0;
          font-size: 1.8rem;
        }

        .section-note {
          background: rgba(157, 78, 221, 0.1);
          border-left: 3px solid #9d4edd;
          padding: 12px;
          margin-bottom: 20px;
          font-size: 0.9rem;
          color: #d0c8e0;
          line-height: 1.6;
        }

        .xp-summary-box {
          background: rgba(123, 44, 191, 0.2);
          border: 1px solid #7b2cbf;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .xp-row {
          display: flex;
          justify-content: space-between;
          color: #e0d8f0;
          font-size: 1.05rem;
          margin-bottom: 6px;
        }

        .xp-row:last-child { margin-bottom: 0; }

        .xp-value { color: #c9a962; font-weight: bold; }

        .stat-filter-section { margin-bottom: 20px; }

        .stat-filter-section h4 {
          color: #c9a962;
          margin: 0 0 12px 0;
          font-size: 1.2rem;
        }

        .stat-selector {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .stat-btn {
          background: rgba(26, 10, 46, 0.7);
          border: 1px solid rgba(123, 44, 191, 0.5);
          border-radius: 8px;
          padding: 10px 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #e0d8f0;
        }

        .stat-btn:hover { border-color: #9d4edd; transform: translateY(-1px); }

        .stat-btn.active {
          background: rgba(123, 44, 191, 0.25);
          border-color: #c9a962;
        }

        .stat-name { font-weight: 600; }
        .stat-value { color: #c9a962; font-weight: 700; }

        .purchased-abilities h4 {
          color: #c9a962;
          margin: 0 0 12px 0;
          font-size: 1.2rem;
        }

        .abilities-grid,
        .browser-abilities-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 12px;
        }

        .ability-card {
          background: rgba(26, 10, 46, 0.7);
          border: 1px solid rgba(123, 44, 191, 0.5);
          border-radius: 12px;
          padding: 12px;
          color: #e0d8f0;
          transition: all 0.2s ease;
        }

        .ability-card.browsing { cursor: pointer; }
        .ability-card:hover { border-color: #9d4edd; transform: translateY(-1px); }
        .ability-card.locked { opacity: 0.65; cursor: not-allowed; }

        .ability-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
        }

        .ability-card-header h5 { margin: 0; font-size: 1.05rem; color: #c9a962; }

        .remove-btn {
          background: transparent;
          border: none;
          color: #e0d8f0;
          font-size: 1.2rem;
          cursor: pointer;
          opacity: 0.8;
        }
        .remove-btn:hover { opacity: 1; color: #ff6b6b; }

        .owned-badge {
          font-size: 0.8rem;
          background: rgba(201, 169, 98, 0.15);
          border: 1px solid rgba(201, 169, 98, 0.5);
          padding: 3px 8px;
          border-radius: 999px;
          color: #c9a962;
          white-space: nowrap;
        }

        .ability-card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;
        }

        .meta-badge {
          font-size: 0.8rem;
          padding: 3px 8px;
          border-radius: 999px;
          border: 1px solid rgba(123, 44, 191, 0.5);
          background: rgba(123, 44, 191, 0.15);
          color: #e0d8f0;
        }

        .meta-badge.cost.insufficient {
          border-color: rgba(255, 107, 107, 0.7);
          background: rgba(255, 107, 107, 0.15);
        }

        .ability-card-stat { font-size: 0.9rem; color: #d0c8e0; margin-bottom: 6px; }
        .ability-card-prereq { font-size: 0.85rem; color: #d0c8e0; margin-bottom: 6px; }

        .ability-card-description,
        .ability-card-effects {
          margin: 0;
          margin-top: 6px;
          font-size: 0.9rem;
          line-height: 1.4;
          color: #e0d8f0;
        }

        .browser-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .browser-header h3 { margin: 0; color: #c9a962; }

        .close-browser-btn {
          background: rgba(123, 44, 191, 0.2);
          border: 1px solid rgba(123, 44, 191, 0.6);
          color: #e0d8f0;
          border-radius: 10px;
          padding: 8px 10px;
          cursor: pointer;
        }

        .browser-filters { display: flex; gap: 10px; margin-bottom: 10px; }

        .search-input {
          flex: 1;
          min-width: 0;
          background: rgba(15, 5, 29, 0.6);
          border: 1px solid rgba(123, 44, 191, 0.6);
          border-radius: 10px;
          padding: 10px 12px;
          color: #e0d8f0;
        }

        .filter-select {
          background: rgba(15, 5, 29, 0.6);
          border: 1px solid rgba(123, 44, 191, 0.6);
          border-radius: 10px;
          padding: 10px 12px;
          color: #e0d8f0;
        }

        .purchase-feedback {
          margin-bottom: 10px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid rgba(123, 44, 191, 0.6);
          background: rgba(123, 44, 191, 0.2);
          color: #e0d8f0;
        }

        .purchase-feedback.success {
          border-color: rgba(60, 200, 120, 0.7);
          background: rgba(60, 200, 120, 0.12);
        }

        .purchase-feedback.error {
          border-color: rgba(255, 107, 107, 0.7);
          background: rgba(255, 107, 107, 0.12);
        }

        .purchase-hint { margin-top: 8px; font-size: 0.85rem; color: #c9a962; }
        .purchase-warning { margin-top: 8px; font-size: 0.85rem; color: #ff6b6b; }

        .empty-state {
          background: rgba(15, 5, 29, 0.5);
          border: 1px dashed rgba(123, 44, 191, 0.6);
          border-radius: 12px;
          padding: 16px;
          color: #d0c8e0;
        }

        .hint { color: #c9a962; margin-top: 6px; }
      `}</style>
    </div>
  );
}

// --- helpers ---
function toCode(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

function stripMd(md) {
  return String(md || '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '')
    .replace(/^\s*#+\s+/gm, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .trim();
}

function formatPrereqs(prereqs) {
  return (prereqs || [])
    .map((p) => {
      switch (p.prereq_type) {
        case 'STAT_MIN':
          return `${p.key_1} ${p.int_value}+`;
        case 'STAT_SUM_MIN':
          return `${p.key_1} + ${p.key_2} ≥ ${p.int_value}`;
        case 'SKILL_RANK_MIN':
          return `${p.key_1} rank ≥ ${p.int_value}`;
        case 'AETHERION_STATE':
          return p.text_value;
        case 'ABILITY_TIER_MIN':
          return `${p.key_1} tier ≥ ${p.int_value}`;
        default:
          return p.text_value || p.prereq_type;
      }
    })
    .filter(Boolean)
    .join('; ');
}

export default AbilitiesTab;
